import dayjs from 'dayjs';
import { rqx } from '../../../../../core/request/API';
import { generateDateRangeIntervals } from './dateRangeInterval';
import { env } from '../../../../../core/Env';

export const DEFAULT_METRICS = [
  'page_media_view',
  'page_follows',
  'page_impressions_unique'
];

export const fetchFacebookInsights = async ({ profileId, accessToken, startDate, endDate, metric_name = '' }) => {
  if (!accessToken) {
    console.error("No access token available in selected profile");
    return null;
  }

  const metricString = metric_name !== '' ? metric_name : DEFAULT_METRICS.join(',');
  // const since = dayjs(startDate).subtract(1, 'day').unix();
  // const until = dayjs(endDate).unix();

  const response = await rqx.fbGraph(
    `${profileId}/insights?metric=${metricString}&period=day&since=${startDate}&until=${endDate}`,
    accessToken
  );
  return response;
};

export const fetchInstagramInsights = async ({ accountId, accessToken, metric_name = 'reach', start_date, end_date }) => {
  if (!accessToken) {
    console.error("No access token available in selected profile");
    return null;
  }
  // const startDate = dayjs(start_date).unix();
  // const endDate = dayjs(end_date).unix();

  const fields = [];
  // if (metric_name == 'follower_count') {
  fields.push(metric_name);
  // } else {
  //   fields.push('profile_views', 'website_clicks', metric_name);
  // }
  const fieldsString = fields.join(',');

  try {
    const response = await rqx.lgGraph(
      `${accountId}/insights?metric=${fieldsString}&period=day&since=${start_date}&until=${end_date}`,
      accessToken
    );
    if (!response?.data) {
      console.error("No data received from Instagram API");
      return null;
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching Instagram posts:", error);
    return null;
  }
};

export const fetchInstagramMessaging = async ({ accountId, accessToken, start_date, end_date }) => {
  if (!accessToken) {
    console.error("No access token available in selected account");
    return null;
  }
  // const startDate = dayjs(start_date).unix();
  // const endDate = dayjs(end_date).unix();

  const response = await rqx.lgGraph(
    `${accountId}/conversations?fields=updated_time&since=${start_date}&until=${end_date}`,
    accessToken
  );
  return response.data;
};

export const processInsightsData = ({ insightsData, startDate, endDate, targetMetric }) => {
  if (!insightsData?.data) return [];

  const resByDate = insightsData.data.reduce((acc, metric) => {
    const metricName = metric.name;
    metric.values.forEach(value => {
      const dateStr = dayjs(value.end_time).subtract(1, 'day').format('YYYY-MM-DD');
      const date = dayjs(dateStr);

      if (date.isSameOrAfter(dayjs(startDate)) &&
        date.isSameOrBefore(dayjs(endDate))) {

        if (!acc[dateStr]) {
          acc[dateStr] = {};
        }
        acc[dateStr][metricName] = value.value;
      }
    });
    return acc;
  }, {});

  return Object.entries(resByDate).map(([date, metrics]) => ({
    date,
    value: metrics[targetMetric]
  }));
};

export const calculatePercentageChange = (oldValue, newValue) => {
  if (oldValue === 0) {
    return Math.round(100 * newValue * 100) / 100;
  }
  return Math.round(((newValue - oldValue) / oldValue) * 100 * 100) / 100;
};

export const fetchAllPosts = async (url, accessToken, totalCount = 0, allPosts = []) => {
  let res;

  if (url.startsWith('https://graph.facebook.com/v24.0/')) {
    // For complete URLs, use fetch directly
    const response = await fetch(url);
    res = await response.json();
  } else {
    // For endpoints, use rqx.fbGraph
    res = await rqx.fbGraph(url, accessToken);
  }

  const posts = res.data || [];
  totalCount += posts.length;
  allPosts = allPosts.concat(posts);

  if (res.paging?.next) {
    return fetchAllPosts(res.paging.next, accessToken, totalCount, allPosts);
  }

  return { totalCount, allPosts };
};

export const fetchAllInstagramPosts = async (url, accessToken, totalCount = 0, allPosts = []) => {
  let res;
  if (url.startsWith('https://graph.instagram.com/v22.0/')) {
    // For complete URLs, use fetch directly
    const response = await fetch(url);
    res = await response.json();
  } else {
    // For endpoints, use rqx.lgGraph
    res = await rqx.lgGraph(url, accessToken);
  }

  const posts = res.data || [];
  totalCount += posts.length;
  allPosts = allPosts.concat(posts);

  if (res.paging?.next) {
    return fetchAllInstagramPosts(res.paging.next, accessToken, totalCount, allPosts);
  }

  return { totalCount, allPosts };
};

export const fetchInstagramViews = async (profileId, accessToken, startDate, endDate) => {
  if (!accessToken) {
    console.error("No access token available in selected account");
    return null;
  }
  const url = `${profileId}/media?fields=id,caption,media_url,timestamp,like_count,comments_count,media_type,username,children{id,media_url,timestamp,like_count,comments_count,media_type,username}&limit=100`;
  const { allPosts } = await fetchAllInstagramPosts(url, accessToken);
  const reach = await rqx.lgGraph(
    `${profileId}/insights?metric=reach&period=day&since=${startDate}&until=${endDate}`,
    accessToken
  );

  // Filter video posts
  const videoPosts = allPosts.filter(post => post.media_type === 'VIDEO' || post.media_type === 'REELS');
  const video_views_by_date = {};

  // Fetch video views for each video post
  for (const post of videoPosts) {
    try {
      const videoInsights = await rqx.lgGraph(
        `${post.id}/insights?metric=video_views&period=lifetime`,
        accessToken
      );

      if (videoInsights?.data?.[0]?.values?.[0]?.value) {
        const views = videoInsights.data[0].values[0].value;
        const postDate = dayjs(post.timestamp);

        if (postDate.isSameOrBefore(dayjs(endDate))) {
          // Add views to all dates from post date until end date
          let currentDate = postDate;
          while (currentDate.isSameOrBefore(dayjs(endDate))) {
            const dateStr = currentDate.format('YYYY-MM-DD');
            video_views_by_date[dateStr] = (video_views_by_date[dateStr] || 0) + views;
            currentDate = currentDate.add(1, 'day');
          }
        }
      }
    } catch (error) {
      console.error(`Error fetching video views for post ${post.id}:`, error);
    }
  }

  // Combine reach and video views data
  const views_by_date = {};
  if (reach?.data?.[0]?.values) {
    reach.data[0].values.forEach(insight => {
      const dateStr = dayjs(insight.end_time).format('YYYY-MM-DD');
      views_by_date[dateStr] = insight.value + (video_views_by_date[dateStr] || 0);
    });
  }

  return views_by_date;
};

export const getFollowsChangePercentage = async (pageId, accessToken) => {
  try {
    // Calculate timestamps for current week and last week
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

    // Convert to Unix timestamps (required by Facebook API)
    const currentWeekStart = Math.floor(oneWeekAgo.getTime() / 1000);
    const currentWeekEnd = Math.floor(now.getTime() / 1000);
    const lastWeekStart = Math.floor(twoWeeksAgo.getTime() / 1000);
    const lastWeekEnd = Math.floor(oneWeekAgo.getTime() / 1000);

    // Fetch current week fans data
    const currentWeekResponse = await rqx.fbGraph(
      `${pageId}/insights/page_fan_adds?since=${currentWeekStart}&until=${currentWeekEnd}&period=day`,
      accessToken
    );

    // Fetch last week fans data
    const lastWeekResponse = await rqx.fbGraph(
      `${pageId}/insights/page_fan_adds?since=${lastWeekStart}&until=${lastWeekEnd}&period=day`,
      accessToken
    );

    if (!currentWeekResponse?.data || !lastWeekResponse?.data) {
      return 0;
    }

    // Sum daily fans added for current week
    const sumFansAdded = (values) => {
      if (!values || values.length === 0) return 0;
      return values.reduce((sum, item) => sum + (item.value || 0), 0);
    };

    const currentWeekFans = sumFansAdded(currentWeekResponse.data[0]?.values);
    const lastWeekFans = sumFansAdded(lastWeekResponse.data[0]?.values);

    const fansChangePct = calculatePercentageChange(lastWeekFans, currentWeekFans);
    return fansChangePct;

  } catch (error) {
    console.error('Error fetching followers change percentage:', error);
    return 0;
  }
};

export const PostDataProcessing = (allPosts, viewsData = []) => {
  const dates = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');
  });

  // Initialize data structures
  const likesAndPosts = dates.map(date => ({
    date,
    likes: 0,
    posts: 0
  }));

  const contentInteraction = dates.map(date => ({
    date,
    views: 0,
    likes: 0,
    comments: 0
  }));

  // Process posts
  let totalLikes = 0;
  let totalFinalLikes = 0;
  let totalPosts = allPosts.length;
  let totalComments = 0;
  let totalShares = 0;
  let totalViews = 0;
  // Split posts into current week and last week
  const currentWeekPosts = [];
  const lastWeekPosts = [];
  
  const today = new Date();
  const startOfCurrentWeek = new Date(today);
  startOfCurrentWeek.setDate(today.getDate() - 6); // 7 days including today

  const startOfLastWeek = new Date(today);
  startOfLastWeek.setDate(today.getDate() - 13); // 7 days before current week

  const endOfLastWeek = new Date(today);
  endOfLastWeek.setDate(today.getDate() - 7); // last day of last week

  allPosts.forEach(post => {
    const postDate = new Date(post.created_time || post.timestamp);

    if (postDate >= startOfCurrentWeek && postDate <= today) {
      currentWeekPosts.push(post);
    } else if (postDate >= startOfLastWeek && postDate < endOfLastWeek) {
      lastWeekPosts.push(post);
    }
  });

  // Calculate metrics for current week
  let currentWeekLikes = 0;
  let currentWeekPostsCount = currentWeekPosts.length;
  currentWeekPosts.forEach(post => {
    currentWeekLikes += post.likes?.summary?.total_count || post.like_count || 0;
  });

  // Calculate metrics for last week
  let lastWeekLikes = 0;
  let lastWeekPostsCount = lastWeekPosts.length;
  lastWeekPosts.forEach(post => {
    lastWeekLikes += post.likes?.summary?.total_count || post.like_count || 0;
  });

  // Calculate percentage changes
  const totalPostsChangePct = calculatePercentageChange(lastWeekPostsCount, currentWeekPostsCount);
  const totalLikesChangePct = calculatePercentageChange(lastWeekLikes, currentWeekLikes);
  const avgLikesChangePct = calculatePercentageChange(
    lastWeekPosts ? lastWeekLikes / lastWeekPosts : 0,
    currentWeekPosts ? currentWeekLikes / currentWeekPostsCount : 0
  );
  const engagementRateChangePct = calculatePercentageChange(
    lastWeekPosts ? (lastWeekLikes / lastWeekPosts) * 100 : 0,
    currentWeekPosts ? (currentWeekLikes / currentWeekPostsCount) * 100 : 0
  );

  // Process all posts for the original calculations
  allPosts.forEach(post => {
    totalFinalLikes += post.likes?.summary?.total_count || post.like_count || 0;
    totalComments += post.comments?.comment_count || post.comments_count || 0;
    totalShares += post.shares?.count || 0;
    totalViews += post.insights?.data?.[0]?.values?.[0]?.value || 0;
    const postDate = new Date(post.created_time || post.timestamp).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');

    const dateIndex = dates.indexOf(postDate);
    if (dateIndex !== -1) {
      // Update likes and posts
      const postLikes = post.likes?.summary?.total_count || post.like_count || 0;
      likesAndPosts[dateIndex].likes += postLikes;
      likesAndPosts[dateIndex].posts += 1;

      // Update content interaction
      if (!viewsData) {
        const views = post.insights?.data?.[0]?.values?.[0]?.value || 0;
        contentInteraction[dateIndex].views += views;
      }
      const comments = post.comments?.data?.length || post.comments_count || 0;
      contentInteraction[dateIndex].likes += postLikes;
      contentInteraction[dateIndex].comments += comments;

      totalLikes += postLikes;
    }
  });

  if (viewsData) {
    contentInteraction.forEach(item => {
      const matchingView = viewsData.find(view => {
        // Parse the ISO date string and format it to DD-MM-YYYY
        const date = new Date(view.end_time);
        const formattedViewDate = date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).replace(/\//g, '-');
        return formattedViewDate === item.date;
      });
      item.views = matchingView?.value || 0;
    });
  }

  // Calculate metrics
  const avgLikesPerPost = Math.floor(totalFinalLikes / totalPosts);
  const engagementRate = Math.round((Number(totalFinalLikes) + Number(totalComments) + Number(totalShares)) / Math.max(Number(totalViews), 1) * 100, 2);

  return {
    total_posts: totalPosts,
    total_posts_change_pct: totalPostsChangePct,
    total_likes: totalFinalLikes,
    total_likes_change_pct: totalLikesChangePct,
    avg_likes_per_post: avgLikesPerPost,
    avg_likes_change_pct: avgLikesChangePct,
    engagement_rate: engagementRate,
    engagement_rate_change_pct: engagementRateChangePct,
    likes_and_posts: likesAndPosts,
    content_interaction: contentInteraction,
  };
}

export const generateLinkClicksData = (startDate, endDate, allPosts) => {
  // Initialize link clicks by date object
  const linkClicksByDate = {};

  // Process each post to extract link clicks
  allPosts.forEach(post => {
    const postDate = dayjs(post.created_time || post.timestamp).format('YYYY-MM-DD');

    // Initialize link clicks for this date if not exists
    if (!linkClicksByDate[postDate]) {
      linkClicksByDate[postDate] = 0;
    }

    // Extract link clicks from post insights
    let linkClicks = 0;
    if (post.insights?.data) {
      const clicksMetric = post.insights.data.find(metric => metric.name === 'post_clicks_by_type');
      if (clicksMetric?.values?.[0]?.value) {
        // Sum up all click types
        linkClicks = Object.values(clicksMetric.values[0].value).reduce((sum, count) => sum + parseInt(count || 0), 0);
      }
    }

    // Add link clicks to the date
    linkClicksByDate[postDate] += linkClicks;
  });

  // Generate data points for the entire date range
  const result = [];
  let currentDate = dayjs(startDate);
  const end = dayjs(endDate);

  while (currentDate.isSameOrBefore(end)) {
    const dateStr = currentDate.format('YYYY-MM-DD');
    result.push({
      date: dateStr,
      value: linkClicksByDate[dateStr] || 0
    });
    currentDate = currentDate.add(1, 'day');
  }
  const dataRange = generateDateRangeIntervals(startDate, endDate, result, 'link_clicks');

  return dataRange;
};

export const fetchUserAuthDetails = async (companyId, userId, platform) => {
  const bdy = {
    user_id: userId,
    company_id: companyId,
    platform: platform
  };

  const response = await rqx.get(`<<tool>>/${env()}/facebookInsights/social_account_details`, bdy);
  if (response) {
    if (platform === '1') {
      const pages = await rqx.fbGraph('me/accounts', response?.data?.access_token, {
        fields: 'id,name,email,picture,access_token'
      });
      const page = pages?.data?.find(page => page?.id === response?.data?.page_id);
      return {
        account_id: null,
        access_token: page?.access_token,
        page_id: page?.id
      };
    } else if (platform === '2') {
      return {
        account_id: response?.data?.account_id,
        access_token: response?.data?.access_token,
        page_id: null
      };
    }
  }
}